import React, { useMemo, useState } from "react";
import { useSEO } from "../hooks/useSEO";

function isEmailValid(email) {
  const e = String(email || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function Contact() {
  useSEO({
    title: "Contact",
    description: "Contactez-nous pour toute question sur nos outils immobiliers gratuits. Suggestions, bugs, demandes de fonctionnalités - nous sommes à votre écoute."
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // Anti-spam simple (champ caché)
  const [website, setWebsite] = useState("");

  const contactEmail = "contact@pdf-converter.fr"; // <-- remplace par ton email

  const errors = useMemo(() => {
    const errs = {};
    if (!name.trim()) errs.name = "Ton nom est requis.";
    if (!email.trim()) errs.email = "Ton email est requis.";
    else if (!isEmailValid(email)) errs.email = "Email invalide.";
    if (!subject.trim()) errs.subject = "Le sujet est requis.";
    if (!message.trim()) errs.message = "Le message est requis.";
    if (message.trim().length < 10)
      errs.message = "Message trop court (min. 10 caractères).";
    return errs;
  }, [name, email, subject, message]);

  const canSend = Object.keys(errors).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot rempli => bot => on ignore
    if (website.trim()) return;

    if (!canSend) return;

    // ✅ MODE 1 : mailto (local, sans backend)
    const body = [`Nom: ${name}`, `Email: ${email}`, "", message].join("\n");

    const mailto = `mailto:${encodeURIComponent(
      contactEmail
    )}?subject=${encodeURIComponent(
      `[Contact] ${subject}`
    )}&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;

    // (Option) reset
    // setName(""); setEmail(""); setSubject(""); setMessage("");
  };

  return (
    <div className="page" style={{ maxWidth: 900 }}>
      <h1 style={{ color: "#1e293b" }}>Contact</h1>
      <p style={{ color: "#64748b", marginTop: 6 }}>
        Une question, une idée d’outil à ajouter, un bug à signaler ? Envoie-moi
        un message.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.2fr 0.8fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        {/* FORM */}
        <form onSubmit={handleSubmit} style={cardStyle}>
          {/* Honeypot (caché) */}
          <div
            style={{
              position: "absolute",
              left: "-5000px",
              top: "auto",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          >
            <label>
              Ne pas remplir
              <input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          <h2 style={{ marginTop: 0, fontSize: 18, color: "#1e293b" }}>Formulaire</h2>

          <Field
            label="Nom"
            value={name}
            setValue={setName}
            placeholder="Ton nom"
            error={errors.name}
          />

          <Field
            label="Email"
            value={email}
            setValue={setEmail}
            placeholder="ton@email.com"
            error={errors.email}
          />

          <Field
            label="Sujet"
            value={subject}
            setValue={setSubject}
            placeholder="Ex: Bug sur l’outil de mensualité"
            error={errors.subject}
          />

          <label style={{ display: "block", marginBottom: 12, color: "#1e293b", fontWeight: 500 }}>
            Message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Décris ton besoin (le plus précisément possible)."
              rows={7}
              style={{
                width: "100%",
                padding: 10,
                marginTop: 6,
                borderRadius: 10,
                border: errors.message ? "1px solid #e57373" : "1px solid #e2e8f0",
                resize: "vertical",
                color: "#1e293b",
              }}
            />
            {errors.message && <div style={errorStyle}>{errors.message}</div>}
          </label>

          <button
            type="submit"
            disabled={!canSend}
            style={{
              ...btnStyle,
              opacity: canSend ? 1 : 0.6,
              cursor: canSend ? "pointer" : "not-allowed",
              fontWeight: 800,
            }}
          >
            Envoyer
          </button>

          <p style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
            En cliquant sur "Envoyer", ton application mail s'ouvre avec le
            message pré-rempli.
          </p>
        </form>

        {/* INFO */}
        <div style={cardStyle}>
          <h2 style={{ marginTop: 0, fontSize: 18, color: "#1e293b" }}>Contact direct</h2>
          <p style={{ marginTop: 6, color: "#64748b" }}>
            Tu peux aussi envoyer un mail directement :
          </p>
          <div style={{ fontWeight: 700, color: "#1e40af" }}>{contactEmail}</div>

          <div
            style={{
              marginTop: 14,
              paddingTop: 14,
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: "#1e293b" }}>Conseil</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Si c’est un bug, précise : l’outil concerné, ce que tu as fait, et
              ce que tu attends.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, setValue, placeholder, error }) {
  return (
    <label style={{ display: "block", marginBottom: 12, color: "#1e293b", fontWeight: 500 }}>
      {label}
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: 10,
          marginTop: 6,
          borderRadius: 10,
          border: error ? "1px solid #e57373" : "1px solid #e2e8f0",
          color: "#1e293b",
        }}
      />
      {error && <div style={errorStyle}>{error}</div>}
    </label>
  );
}

const cardStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 20,
  background: "white",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  position: "relative",
};

const btnStyle = {
  padding: "12px 24px",
  borderRadius: 10,
  border: "none",
  background: "#1e40af",
  color: "white",
  fontSize: 14,
};

const errorStyle = {
  marginTop: 6,
  fontSize: 12,
  color: "#c62828",
};
